/**
 * CrossValidationModal.jsx
 * 교차 검증 결과를 비교하는 풀스크린 모달
 * 
 * 레이아웃: 좌=PDF뷰어 | 우상=1차추출그리드 | 우하=검증그리드
 * - 좌우 경계선 드래그로 비율 조정 가능
 * - AUIGrid 고급 기능: 인라인 필터, 정렬, 숫자 포맷, 컬럼 이동
 * - ⭐ 행 클릭 동기화: 왼쪽 클릭 → 오른쪽 동일 행 자동 선택
 * - ⭐ 단어 수준 diff 하이라이팅: 오른쪽 그리드에서 차이 표시
 * - 동기화된 스크롤 (세로 + 가로)
 * - 불일치 셀 빨간 배경 강조
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactPDFViewer from './ReactPDFViewer';

// ============================================
// 🔧 단어 수준 diff 알고리즘
// ============================================
function computeWordDiff(baseStr, compareStr) {
    const safeBase = String(baseStr ?? '');
    const safeCompare = String(compareStr ?? '');

    if (safeBase === safeCompare) return { leftHtml: escapeHtml(safeBase), rightHtml: escapeHtml(safeCompare), hasDiff: false };
    if (!safeBase && !safeCompare) return { leftHtml: '', rightHtml: '', hasDiff: false };
    if (!safeBase) return { leftHtml: '<span class="cv-diff-empty">(없음)</span>', rightHtml: `<span class="cv-diff-add">${escapeHtml(safeCompare)}</span>`, hasDiff: true };
    if (!safeCompare) return { leftHtml: `<span class="cv-diff-del-left">${escapeHtml(safeBase)}</span>`, rightHtml: '<span class="cv-diff-empty">(없음)</span>', hasDiff: true };

    // 숫자 비교 (정수/소수 비교)
    const numBase = parseFloat(safeBase.replace(/,/g, ''));
    const numCompare = parseFloat(safeCompare.replace(/,/g, ''));
    if (!isNaN(numBase) && !isNaN(numCompare)) {
        if (numBase === numCompare) return { leftHtml: escapeHtml(safeBase), rightHtml: escapeHtml(safeCompare), hasDiff: false };
        return {
            leftHtml: `<span class="cv-diff-del-left">${escapeHtml(safeBase)}</span>`,
            rightHtml: `<span class="cv-diff-add-right">${escapeHtml(safeCompare)}</span>`,
            hasDiff: true
        };
    }

    // 문자열: 글자 수준 비교
    return charLevelDiff(safeBase, safeCompare);
}

function escapeHtml(str) {
    if (str === null || str === undefined) return '';
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// 글자 수준 diff (LCS 기반) → 양방향 HTML 생성
function charLevelDiff(base, compare) {
    const a = String(base);
    const b = String(compare);

    if (a === b) return { leftHtml: escapeHtml(a), rightHtml: escapeHtml(b), hasDiff: false };

    // 짧은 문자열에 대해 LCS 사용
    if (a.length <= 500 && b.length <= 500) {
        const lcs = computeLCS(a, b);
        const { leftHtml, rightHtml } = buildDiffHtml(a, b, lcs);
        return { leftHtml, rightHtml, hasDiff: true };
    }

    // 긴 문자열은 단어 단위 비교
    return wordLevelFallback(a, b);
}

// LCS (Longest Common Subsequence) 계산
function computeLCS(a, b) {
    const m = a.length, n = b.length;
    // 메모리 최적화: 2행만 사용
    let prev = new Array(n + 1).fill(0);
    let curr = new Array(n + 1).fill(0);

    for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
            if (a[i - 1] === b[j - 1]) {
                curr[j] = prev[j - 1] + 1;
            } else {
                curr[j] = Math.max(prev[j], curr[j - 1]);
            }
        }
        [prev, curr] = [curr, new Array(n + 1).fill(0)];
    }

    // 역추적을 위해 전체 DP 테이블 필요
    const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
    for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
            if (a[i - 1] === b[j - 1]) {
                dp[i][j] = dp[i - 1][j - 1] + 1;
            } else {
                dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
            }
        }
    }

    // LCS 역추적
    const lcs = [];
    let i = m, j = n;
    while (i > 0 && j > 0) {
        if (a[i - 1] === b[j - 1]) {
            lcs.unshift({ char: a[i - 1], ai: i - 1, bi: j - 1 });
            i--; j--;
        } else if (dp[i - 1][j] > dp[i][j - 1]) {
            i--;
        } else {
            j--;
        }
    }
    return lcs;
}

// LCS 기반 diff HTML 생성 (양방향: 왼쪽 + 오른쪽)
function buildDiffHtml(base, compare, lcs) {
    // ⭐ 왼쪽 (기준): 삭제/변경된 글자에 취소선
    const leftParts = [];
    let ai = 0, lcsIdxL = 0;
    while (ai < base.length) {
        if (lcsIdxL < lcs.length && lcs[lcsIdxL].ai === ai) {
            leftParts.push(escapeHtml(base[ai]));
            lcsIdxL++;
            ai++;
        } else {
            let diffChars = '';
            while (ai < base.length && (lcsIdxL >= lcs.length || lcs[lcsIdxL].ai !== ai)) {
                diffChars += base[ai];
                ai++;
            }
            leftParts.push(`<span class="cv-diff-del-left">${escapeHtml(diffChars)}</span>`);
        }
    }

    // ⭐ 오른쪽 (검증): 추가/변경된 글자에 강조
    const rightParts = [];
    let bi = 0, lcsIdxR = 0;
    while (bi < compare.length) {
        if (lcsIdxR < lcs.length && lcs[lcsIdxR].bi === bi) {
            rightParts.push(escapeHtml(compare[bi]));
            lcsIdxR++;
            bi++;
        } else {
            let diffChars = '';
            while (bi < compare.length && (lcsIdxR >= lcs.length || lcs[lcsIdxR].bi !== bi)) {
                diffChars += compare[bi];
                bi++;
            }
            rightParts.push(`<span class="cv-diff-add-right">${escapeHtml(diffChars)}</span>`);
        }
    }

    return { leftHtml: leftParts.join(''), rightHtml: rightParts.join('') };
}

// 긴 문자열용 단어 수준 fallback (양방향)
function wordLevelFallback(base, compare) {
    const wordsA = base.split(/(\s+)/);
    const wordsB = compare.split(/(\s+)/);
    const leftParts = [];
    const rightParts = [];
    let hasDiff = false;

    const maxLen = Math.max(wordsA.length, wordsB.length);
    for (let i = 0; i < maxLen; i++) {
        const wa = wordsA[i] || '';
        const wb = wordsB[i] || '';
        if (wa === wb) {
            leftParts.push(escapeHtml(wa));
            rightParts.push(escapeHtml(wb));
        } else {
            hasDiff = true;
            if (wa) leftParts.push(`<span class="cv-diff-del-left">${escapeHtml(wa)}</span>`);
            if (wb) rightParts.push(`<span class="cv-diff-add-right">${escapeHtml(wb)}</span>`);
        }
    }

    return { leftHtml: leftParts.join(''), rightHtml: rightParts.join(''), hasDiff };
}


// ============================================
// ⭐ Main Component
// ============================================
const CrossValidationModal = ({ isOpen, onClose, primaryItems, crossItems, crossValidation, columnFields, pdfFile }) => {
    const leftGridId = '#cv_left_grid';
    const rightGridId = '#cv_right_grid';
    const [filterMode, setFilterMode] = useState('all'); // 'all' | 'mismatch-rows' | 'mismatch-words'
    const [showMismatchBar, setShowMismatchBar] = useState(true); // 상단 불일치 바
    const [activeFieldGroup, setActiveFieldGroup] = useState(null); // 현재 선택된 필드명
    const [fieldCursor, setFieldCursor] = useState(0); // 해당 필드 내 순차 위치
    const scrollSyncLock = useRef(null); // 'left' | 'right' | null
    const scrollTimer = useRef(null);
    const hScrollSyncLock = useRef(false);
    const rowSelectLock = useRef(false);
    const syncedRowRef = useRef(-1);

    // 🔧 좌우 패널 드래그 리사이저
    const [leftPanelWidth, setLeftPanelWidth] = useState(35); // 왼쪽 PDF 패널 비율 (%)
    const isDraggingRef = useRef(false);
    const containerRef = useRef(null);
    // 📄 PDF 뷰어 페이지 상태
    const [pdfPageNumber, setPdfPageNumber] = useState(1);
    const [pdfTotalPages, setPdfTotalPages] = useState(0);

    // 🔧 드래그 리사이저 핸들러
    const handleSplitterMouseDown = useCallback((e) => {
        e.preventDefault();
        isDraggingRef.current = true;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';

        const handleMouseMove = (moveEvent) => {
            if (!isDraggingRef.current || !containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            const x = moveEvent.clientX - rect.left;
            const pct = Math.max(15, Math.min(70, (x / rect.width) * 100));
            setLeftPanelWidth(pct);
        };

        const handleMouseUp = () => {
            isDraggingRef.current = false;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            // 그리드 리프레시 (크기 변경 반영)
            try {
                if (window.AUIGrid?.isCreated(leftGridId)) window.AUIGrid.resize(leftGridId);
                if (window.AUIGrid?.isCreated(rightGridId)) window.AUIGrid.resize(rightGridId);
            } catch (e) { /* ignore */ }
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, []);

    // 컬럼 목록 생성 (meta 필드 제외)
    const getDisplayFields = useCallback(() => {
        if (columnFields && columnFields.length > 0) return columnFields;
        const skipKeys = new Set(['pageNumber', 'P', 'p', 'page', 'differences', 'metadata', 'documentType', 'idx', '_rowIndex', '_hasDiff']);
        const fields = new Set();
        (primaryItems || []).forEach(item => {
            Object.keys(item).forEach(k => {
                if (!skipKeys.has(k) && !k.startsWith('_')) fields.add(k);
            });
        });
        return Array.from(fields);
    }, [primaryItems, columnFields]);

    // 불일치 필드 맵 구축: { rowIdx: Set('fieldA', 'fieldB') }
    const getMismatchMap = useCallback(() => {
        const map = {};
        (primaryItems || []).forEach((item, idx) => {
            if (item.differences) {
                const diffFields = Object.keys(item.differences).filter(k => item.differences[k].match === false);
                if (diffFields.length > 0) {
                    map[idx] = new Set(diffFields);
                }
            }
        });
        return map;
    }, [primaryItems]);

    // 그리드 생성 및 데이터 설정
    useEffect(() => {
        if (!isOpen || !window.AUIGrid) return;

        const fields = getDisplayFields();
        const mismatchMap = getMismatchMap();

        // 데이터 필터링
        let leftData = primaryItems || [];
        let rightData = crossItems || [];

        if (filterMode === 'mismatch-rows') {
            const mismatchRows = Object.keys(mismatchMap).map(Number);
            leftData = leftData.filter((_, idx) => mismatchRows.includes(idx));
            rightData = rightData.filter((_, idx) => mismatchRows.includes(idx));
        } else if (filterMode === 'mismatch-words') {
            // 단어 수준 불일치: 글자가 다른 행만 필터링
            const wordDiffRows = [];
            leftData.forEach((lItem, idx) => {
                const rItem = rightData[idx];
                if (!rItem) return;
                const hasCharDiff = fields.some(f => {
                    const lv = String(lItem[f] ?? '');
                    const rv = String(rItem[f] ?? '');
                    if (lv === rv || lv.toLowerCase() === rv.toLowerCase()) return false;
                    // 글자 수준 비교: 하나라도 다른 글자가 있는지 (대소문자 무시)
                    for (let i = 0; i < Math.max(lv.length, rv.length); i++) {
                        if ((lv[i] || '').toLowerCase() !== (rv[i] || '').toLowerCase()) return true;
                    }
                    return false;
                });
                if (hasCharDiff) wordDiffRows.push(idx);
            });
            leftData = leftData.filter((_, idx) => wordDiffRows.includes(idx));
            rightData = rightData.filter((_, idx) => wordDiffRows.includes(idx));
        }

        // 숫자 필드 판별
        const isNumericField = (field) =>
            /amount|price|qty|quantity|weight|cbm|count|total|vat|tax|cost|fee|charge|수량|단가|금액|중량|합계/i.test(field);

        // ⭐ 숫자 서식 동치 비교: "1,928.00" === "1928" (콤마/소수점 무시)
        const isNumericEqual = (a, b) => {
            const na = parseFloat(String(a).replace(/,/g, ''));
            const nb = parseFloat(String(b).replace(/,/g, ''));
            return !isNaN(na) && !isNaN(nb) && na === nb;
        };

        // ⭐ 칸럼 너비 계산: 필드 유형별 지능적 맞춤
        const calcColumnWidth = (field, data) => {
            const headerLen = field.length;
            const isNum = isNumericField(field);
            const isDesc = /description|goods|품명|품목|상품|remarks|비고/i.test(field);
            const isCode = /^(No|HS|code|번호)$/i.test(field) || /HS.*Code/i.test(field);

            let maxDataLen = 0;
            (data || []).forEach(item => {
                const val = String(item[field] ?? '');
                if (val.length > maxDataLen) maxDataLen = val.length;
            });

            const headerWidth = headerLen * 7 + 30; // 헤더 텍스트 + 필터 아이콘

            if (isNum) {
                // 숫자 필드: 최소 85px (콤마 포함 숫자 표시), 데이터 기준으로 확장
                const dataWidth = maxDataLen * 8 + 20;
                return Math.max(85, headerWidth, dataWidth);
            }

            if (isDesc) {
                // Description/Goods: 최대 250px로 제한 (너무 넓으면 다른 컬럼 압축)
                const dataWidth = Math.min(maxDataLen * 7 + 20, 250);
                return Math.max(headerWidth, dataWidth);
            }

            if (isCode) {
                // 코드 필드: 컴팩트
                return Math.max(60, headerWidth);
            }

            if (maxDataLen === 0) {
                return headerWidth;
            }

            // 일반 필드: 데이터 기준, 최대 200px
            const dataWidth = Math.min(maxDataLen * 7 + 20, 200);
            return Math.max(headerWidth, dataWidth);
        };

        // ============================================
        // 좌측 컬럼 레이아웃 (기준 — 일반 표시)
        // ============================================
        const createLeftColumns = () => {
            const cols = [
                {
                    dataField: '_rowNum', headerText: 'No', width: 45, editable: false,
                    labelFunction: (rowIndex) => rowIndex + 1,
                    style: 'center-align',
                    filter: { showIcon: false }
                }
            ];

            fields.forEach(field => {
                const isNum = isNumericField(field);
                cols.push({
                    dataField: field,
                    headerText: field,
                    width: calcColumnWidth(field, leftData),
                    editable: false,
                    dataType: 'string',
                    filter: { showIcon: true, inline: true },
                    // ⭐ TemplateRenderer (공식 패턴: renderer에 type만 + 별도 labelFunction)
                    renderer: {
                        type: 'TemplateRenderer'
                    },
                    labelFunction: function (rowIndex, columnIndex, value, headerText, item) {
                        const leftVal = String(value ?? '');
                        const rightItem = rightData[rowIndex];
                        if (!rightItem) return leftVal;
                        const rightVal = String(rightItem[field] ?? '');
                        const isEmptyV = (v) => !v || v === '0' || v === '0.0' || v === '0.00';
                        if (isEmptyV(leftVal) && isEmptyV(rightVal)) return leftVal;
                        if (leftVal === rightVal || leftVal.toLowerCase() === rightVal.toLowerCase()) return leftVal;
                        // ⭐ 숫자 서식 차이(콤마/소수점)는 동일 취급
                        if (isNumericEqual(leftVal, rightVal)) return leftVal;
                        // 글자 단위 diff: 다른 글자만 빨간색 (대소문자 무시)
                        let html = '';
                        const maxLen = Math.max(leftVal.length, rightVal.length);
                        let i = 0;
                        while (i < leftVal.length) {
                            const ch = leftVal[i];
                            const esc = ch === '&' ? '&amp;' : ch === '<' ? '&lt;' : ch === '>' ? '&gt;' : ch;
                            if (i < rightVal.length && leftVal[i].toLowerCase() === rightVal[i].toLowerCase()) {
                                html += esc;
                            } else {
                                html += `<span style="color:#DC2626;font-weight:700;">${esc}</span>`;
                            }
                            i++;
                        }
                        return html;
                    },
                    style: isNum ? 'right-align' : 'left-align',
                });
            });
            return cols;
        };

        // ============================================
        // ⭐ 우측 컬럼 레이아웃 (비교 — diff 하이라이팅)
        // ============================================
        const createRightColumns = () => {
            const cols = [
                {
                    dataField: '_rowNum', headerText: 'No', width: 45, editable: false,
                    labelFunction: (rowIndex) => rowIndex + 1,
                    style: 'center-align',
                    filter: { showIcon: false },
                    // ⭐ 동기화된 행 하이라이트
                    styleFunction: (rowIndex) => {
                        return rowIndex === syncedRowRef.current ? 'cv-synced-row center-align' : 'center-align';
                    }
                }
            ];

            fields.forEach(field => {
                const isNum = isNumericField(field);
                cols.push({
                    dataField: field,
                    headerText: field,
                    width: calcColumnWidth(field, rightData),
                    editable: false,
                    dataType: 'string',
                    filter: { showIcon: true, inline: true },
                    // ⭐ TemplateRenderer (공식 패턴: renderer에 type만 + 별도 labelFunction)
                    renderer: {
                        type: 'TemplateRenderer'
                    },
                    labelFunction: function (rowIndex, columnIndex, value, headerText, item) {
                        const rightVal = String(value ?? '');
                        const leftItem = leftData[rowIndex];
                        if (!leftItem) return rightVal;
                        const leftVal = String(leftItem[field] ?? '');
                        const isEmptyR = (v) => !v || v === '0' || v === '0.0' || v === '0.00';
                        if (isEmptyR(leftVal) && isEmptyR(rightVal)) return rightVal;
                        if (leftVal === rightVal || leftVal.toLowerCase() === rightVal.toLowerCase()) return rightVal;
                        // ⭐ 숫자 서식 차이(콤마/소수점)는 동일 취급
                        if (isNumericEqual(leftVal, rightVal)) return rightVal;
                        // 글자 단위 diff: 다른 글자만 빨간색 (대소문자 무시)
                        let html = '';
                        let i = 0;
                        while (i < rightVal.length) {
                            const ch = rightVal[i];
                            const esc = ch === '&' ? '&amp;' : ch === '<' ? '&lt;' : ch === '>' ? '&gt;' : ch;
                            if (i < leftVal.length && leftVal[i].toLowerCase() === rightVal[i].toLowerCase()) {
                                html += esc;
                            } else {
                                html += `<span style="color:#DC2626;font-weight:700;">${esc}</span>`;
                            }
                            i++;
                        }
                        return html;
                    },
                    styleFunction: function (rowIndex, columnIndex, value, headerText, item) {
                        const isSynced = rowIndex === syncedRowRef.current;
                        if (isSynced) return 'cv-synced-row';
                        return isNum ? 'right-align' : 'left-align';
                    }
                });
            });
            return cols;
        };

        // ⭐ AUIGrid 고급 Props
        const gridProps = {
            height: '100%',
            headerHeight: 32,
            rowHeight: 28,
            fillColumnSizeMode: true,
            hScrollPolicy: 'auto',
            showRowNumColumn: false,
            showStateColumn: false,
            editable: false,
            enableFilter: true,
            enableSorting: true,
            enableMovingColumn: true,
            enableColumnResize: true,  // ⭐ 헤더 경계 드래그 리사이즈 허용
            selectionMode: 'singleRow',
            enableDrag: false,
            showFooter: true,
            footerPosition: 'bottom',
            footerHeight: 24,
            noDataMessage: '데이터 없음',
            useContextMenu: true
        };

        // 타이밍: DOM이 렌더링된 후에 생성
        const timer = setTimeout(() => {
            try {
                // 좌측 그리드 (1차 모델)
                if (window.AUIGrid.isCreated(leftGridId)) {
                    window.AUIGrid.destroy(leftGridId);
                }
                const leftGrid = window.AUIGrid.create(leftGridId, createLeftColumns(), gridProps);
                window.AUIGrid.setGridData(leftGridId, leftData);

                // ⭐ 푸터 합계 설정 (좌/우 합계 비교 → 다르면 빨간색)
                const computeSum = (data, field) => {
                    let sum = 0;
                    (data || []).forEach(item => {
                        const v = parseFloat(String(item[field] ?? '').replace(/,/g, ''));
                        if (!isNaN(v)) sum += v;
                    });
                    return sum;
                };

                const setGridFooter = (gridRef, myData, otherData) => {
                    try {
                        const fields = getDisplayFields();
                        const footerLayout = [];
                        fields.forEach((field) => {
                            const isNum = /amount|price|qty|quantity|weight|cbm|count|total|vat|tax|cost|fee|charge|수량|단가|금액|중량|순중량|총중량|부피|합계|세금|비용|갯수|개수|Unit.*Price|Net.*Weight|Gross.*Weight|Netweight|Grossweight|Package/i.test(field);
                            if (isNum) {
                                const mySum = computeSum(myData, field);
                                const otherSum = computeSum(otherData, field);
                                const isDiff = Math.abs(mySum - otherSum) > 0.001;
                                footerLayout.push({
                                    dataField: field,
                                    positionField: field,
                                    operation: "SUM",
                                    formatString: "#,##0.##",
                                    style: isDiff ? 'cv-footer-diff' : 'cv-footer-normal'
                                });
                            }
                        });
                        console.log('📊 [CV Footer] fields:', fields, 'numeric:', footerLayout.map(f => f.dataField));
                        if (footerLayout.length > 0) {
                            window.AUIGrid.setFooter(gridRef, footerLayout);
                            window.AUIGrid.refresh(gridRef);
                        }
                    } catch (e) {
                        console.warn('📊 [CV Footer] ERROR:', e);
                    }
                };
                setGridFooter(leftGrid, leftData, rightData);

                // 우측 그리드 (2차 모델)
                if (window.AUIGrid.isCreated(rightGridId)) {
                    window.AUIGrid.destroy(rightGridId);
                }
                const rightGrid = window.AUIGrid.create(rightGridId, createRightColumns(), gridProps);
                window.AUIGrid.setGridData(rightGridId, rightData);
                setGridFooter(rightGrid, rightData, leftData);

                // ========================================
                // ⭐ 왼쪽 기준 셀 동기화:
                //    syncedRowRef 업데이트 → refresh()로 styleFunction 전체 재평가
                //    setRowPosition으로 해당 행으로 스크롤
                //    refresh()는 데이터 변경 없음 → 부작용 없음
                // ========================================

                const syncRightGrid = (rowIndex) => {
                    try {
                        if (rowIndex === undefined || rowIndex === null || rowIndex < 0) return;
                        if (!window.AUIGrid.isCreated(rightGridId)) return;
                        if (syncedRowRef.current === rowIndex) return;
                        syncedRowRef.current = rowIndex;
                        // ⭐ refresh → 모든 보이는 셀의 styleFunction 재평가
                        window.AUIGrid.refresh(rightGridId);
                        // ⭐ 해당 행으로 스크롤
                        window.AUIGrid.setRowPosition(rightGridId, rowIndex);
                        console.log('[CV Sync] ✅ synced row:', rowIndex);
                    } catch (e) { console.warn('[CV Sync] ERROR:', e); }
                };

                // 1) 마우스 클릭 동기화 (event.rowIndex 사용 - 확인됨)
                window.AUIGrid.bind(leftGridId, 'cellClick', (event) => {
                    syncRightGrid(event.rowIndex);
                });

                // 2) 키보드 동기화 (화살표 키 등)
                const leftGridEl = document.querySelector(leftGridId);
                if (leftGridEl) {
                    leftGridEl.addEventListener('keydown', (e) => {
                        if ([37, 38, 39, 40, 9, 13].includes(e.keyCode)) {
                            setTimeout(() => {
                                try {
                                    const sel = window.AUIGrid.getSelectedIndex(leftGridId);
                                    console.log('[CV Sync] keydown sel:', JSON.stringify(sel));
                                    if (sel && sel.length > 0) {
                                        // sel 구조 확인: 숫자 또는 객체
                                        const idx = typeof sel[0] === 'number' ? sel[0] : (sel[0].rowIndex ?? sel[0]);
                                        syncRightGrid(idx);
                                    }
                                } catch (err) { console.warn('[CV Sync] keydown err:', err); }
                            }, 50);
                        }
                    }, true);
                }

                // ========================================
                // 🔧 동기화 스크롤 (세로 + 가로) - 디바운스 락 적용
                // ========================================
                const clearScrollTimer = () => {
                    if (scrollTimer.current) {
                        clearTimeout(scrollTimer.current);
                        scrollTimer.current = null;
                    }
                };

                /*
                                window.AUIGrid.bind(leftGridId, 'vScrollChange', (event) => {
                                    // 오른쪽이 주도 중이면 무시
                                    if (scrollSyncLock.current === 'right') return;
                
                                    // 왼쪽 주도 설정
                                    scrollSyncLock.current = 'left';
                                    clearScrollTimer();
                
                                    try {
                                        if (window.AUIGrid.isCreated(rightGridId)) {
                                            window.AUIGrid.setRowPosition(rightGridId, event.position);
                                        }
                                    } catch (e) {  }
                
                                    // 스크롤 종료 감지 (200ms 후 락 해제)
                                    scrollTimer.current = setTimeout(() => {
                                        scrollSyncLock.current = null;
                                    }, 200);
                                });
                
                                window.AUIGrid.bind(rightGridId, 'vScrollChange', (event) => {
                                    // 왼쪽이 주도 중이면 무시
                                    if (scrollSyncLock.current === 'left') return;
                
                                    // 오른쪽 주도 설정
                                    scrollSyncLock.current = 'right';
                                    clearScrollTimer();
                
                                    try {
                                        if (window.AUIGrid.isCreated(leftGridId)) {
                                            window.AUIGrid.setRowPosition(leftGridId, event.position);
                                        }
                                    } catch (e) {  }
                
                                    // 스크롤 종료 감지 (200ms 후 락 해제)
                                    scrollTimer.current = setTimeout(() => {
                                        scrollSyncLock.current = null;
                                    }, 200);
                                });
                */

                // 가로 스크롤 동기화
                window.AUIGrid.bind(leftGridId, 'hScrollChange', (event) => {
                    if (hScrollSyncLock.current) return;
                    hScrollSyncLock.current = true;
                    try {
                        if (window.AUIGrid.isCreated(rightGridId)) {
                            window.AUIGrid.setHScrollPosition(rightGridId, event.position);
                        }
                    } catch (e) { /* ignore */ }
                    setTimeout(() => { hScrollSyncLock.current = false; }, 50);
                });

                window.AUIGrid.bind(rightGridId, 'hScrollChange', (event) => {
                    if (hScrollSyncLock.current) return;
                    hScrollSyncLock.current = true;
                    try {
                        if (window.AUIGrid.isCreated(leftGridId)) {
                            window.AUIGrid.setHScrollPosition(leftGridId, event.position);
                        }
                    } catch (e) { /* ignore */ }
                    setTimeout(() => { hScrollSyncLock.current = false; }, 50);
                });

            } catch (e) {
                console.error('[CrossValidation] Grid creation error:', e);
            }
        }, 100);

        return () => {
            clearTimeout(timer);
            try {
                if (window.AUIGrid.isCreated(leftGridId)) window.AUIGrid.destroy(leftGridId);
                if (window.AUIGrid.isCreated(rightGridId)) window.AUIGrid.destroy(rightGridId);
            } catch (e) { /* cleanup */ }
        };
    }, [isOpen, primaryItems, crossItems, filterMode, getDisplayFields, getMismatchMap]);

    // ⭐ ESC 키로 닫기
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    // ⭐ 불일치 리스트 생성 (Hook은 early return 전에 위치해야 함)
    const mismatchList = React.useMemo(() => {
        const list = [];
        const fields = getDisplayFields();
        (primaryItems || []).forEach((lItem, rowIdx) => {
            const rItem = (crossItems || [])[rowIdx];
            if (!rItem) return;
            fields.forEach(field => {
                const lv = String(lItem[field] ?? '');
                const rv = String(rItem[field] ?? '');
                const isEmptyVal = (v) => !v || v === '0' || v === '0.0' || v === '0.00';
                if (isEmptyVal(lv) && isEmptyVal(rv)) return;
                if (lv !== rv) {
                    if (lv.toLowerCase() === rv.toLowerCase()) return;
                    const numL = parseFloat(lv.replace(/,/g, ''));
                    const numR = parseFloat(rv.replace(/,/g, ''));
                    if (!isNaN(numL) && !isNaN(numR) && numL === numR) return;
                    list.push({ rowIdx, field, leftVal: lv, rightVal: rv });
                }
            });
        });
        return list;
    }, [primaryItems, crossItems, getDisplayFields]);

    // ⭐ 필드(열)별 그룹핑: { fieldName: [{ rowIdx, leftVal, rightVal }, ...] }
    const mismatchByField = React.useMemo(() => {
        const map = new Map();
        mismatchList.forEach(m => {
            if (!map.has(m.field)) map.set(m.field, []);
            map.get(m.field).push(m);
        });
        return map;
    }, [mismatchList]);

    // ⭐ 필드 내 고유 값 패턴 (leftVal → rightVal) 그룹핑 + 중복 제거
    const getFieldPatterns = useCallback((fieldName) => {
        const items = mismatchByField.get(fieldName) || [];
        const patternMap = new Map(); // key: "leftVal|||rightVal"
        items.forEach(m => {
            const key = `${m.leftVal}|||${m.rightVal}`;
            if (!patternMap.has(key)) {
                patternMap.set(key, { leftVal: m.leftVal, rightVal: m.rightVal, rows: [] });
            }
            patternMap.get(key).rows.push(m.rowIdx);
        });
        return Array.from(patternMap.values());
    }, [mismatchByField]);

    // ⭐ 필드 그룹 클릭 → 순차 탐색 (같은 필드 재클릭 시 다음 행으로)
    const handleFieldClick = useCallback((fieldName) => {
        const items = mismatchByField.get(fieldName);
        if (!items || items.length === 0) return;

        let nextCursor = 0;
        if (activeFieldGroup === fieldName) {
            nextCursor = (fieldCursor + 1) % items.length;
        }
        setActiveFieldGroup(fieldName);
        setFieldCursor(nextCursor);

        const target = items[nextCursor];
        try {
            if (window.AUIGrid.isCreated(leftGridId)) {
                window.AUIGrid.setRowPosition(leftGridId, target.rowIdx);
                window.AUIGrid.setSelectionByIndex(leftGridId, target.rowIdx, target.field);
            }
            if (window.AUIGrid.isCreated(rightGridId)) {
                syncedRowRef.current = target.rowIdx;
                window.AUIGrid.setRowPosition(rightGridId, target.rowIdx);
                window.AUIGrid.refresh(rightGridId);
            }
        } catch (e) { console.warn('[MismatchNav]', e); }
    }, [mismatchByField, activeFieldGroup, fieldCursor]);

    // ⭐ 패턴별 클릭 → 해당 패턴의 행들만 순환
    const [activePatternKey, setActivePatternKey] = useState(null);
    const [patternCursor, setPatternCursor] = useState(0);
    const handlePatternClick = useCallback((fieldName, pattern) => {
        const key = `${fieldName}|||${pattern.leftVal}|||${pattern.rightVal}`;
        let nextCursor = 0;
        if (activePatternKey === key) {
            nextCursor = (patternCursor + 1) % pattern.rows.length;
        }
        setActivePatternKey(key);
        setPatternCursor(nextCursor);
        setActiveFieldGroup(fieldName);

        const rowIdx = pattern.rows[nextCursor];
        try {
            if (window.AUIGrid.isCreated(leftGridId)) {
                window.AUIGrid.setRowPosition(leftGridId, rowIdx);
                window.AUIGrid.setSelectionByIndex(leftGridId, rowIdx, fieldName);
            }
            if (window.AUIGrid.isCreated(rightGridId)) {
                syncedRowRef.current = rowIdx;
                window.AUIGrid.setRowPosition(rightGridId, rowIdx);
                window.AUIGrid.refresh(rightGridId);
            }
        } catch (e) { console.warn('[MismatchNav]', e); }
    }, [activePatternKey, patternCursor]);

    if (!isOpen) return null;

    const matchRate = crossValidation?.matchRate ?? 0;
    const totalCompared = crossValidation?.totalCompared ?? 0;
    const mismatches = crossValidation?.mismatches ?? 0;
    const primaryModel = crossValidation?.primaryModel || '1차 모델';
    const secondModel = crossValidation?.secondModel || '2차 모델';
    const elapsed = crossValidation?.elapsed ? `${(crossValidation.elapsed / 1000).toFixed(1)}초` : '';

    const getMatchColor = (rate) => {
        if (rate >= 95) return '#0D9488';
        if (rate >= 80) return '#D97706';
        return '#DC2626';
    };


    return (
        <div onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 10000,
            display: 'flex', flexDirection: 'column', cursor: 'pointer'
        }}>
            <div onClick={(e) => e.stopPropagation()} style={{
                height: 40, backgroundColor: '#FFF', color: '#202124',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '0 16px', flexShrink: 0, borderBottom: '1px solid #DADCE0',
                cursor: 'default'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>교차 검증 비교</span>
                    <span style={{
                        padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                        backgroundColor: matchRate >= 95 ? '#1A73E81A' : matchRate >= 80 ? '#E8710A1A' : '#D930251A',
                        color: matchRate >= 95 ? '#1A73E8' : matchRate >= 80 ? '#E8710A' : '#D93025',
                    }}>
                        일치율 {matchRate}%
                    </span>
                    <span style={{ fontSize: 12, color: '#5F6368' }}>
                        {totalCompared - mismatches}/{totalCompared} 일치 · 불일치 {mismatches}건
                        {elapsed && ` · ${elapsed}`}
                    </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {/* 불일치 바 토글 */}
                    <button
                        onClick={() => setShowMismatchBar(v => !v)}
                        title="불일치 리스트 보기/숨기기"
                        style={{
                            padding: '3px 10px', borderRadius: 3, border: 'none', cursor: 'pointer',
                            fontSize: 11, fontWeight: 600, transition: 'all 0.15s',
                            backgroundColor: showMismatchBar ? '#FBBC04' : '#F1F3F4',
                            color: showMismatchBar ? '#1F1F1F' : '#5F6368',
                        }}
                    >
                        📋 불일치 {mismatchList.length}건
                    </button>

                    {/* 필터 모드 */}
                    <div style={{ display: 'flex', gap: 1, backgroundColor: '#F1F3F4', borderRadius: 4, padding: 1 }}>
                        {[
                            { key: 'all', label: '전체' },
                            { key: 'mismatch-rows', label: '불일치 행' },
                            { key: 'mismatch-words', label: '불일치 단어' }
                        ].map(opt => (
                            <button key={opt.key}
                                onClick={() => setFilterMode(opt.key)}
                                style={{
                                    padding: '3px 10px', borderRadius: 3, border: 'none', cursor: 'pointer',
                                    fontSize: 11, fontWeight: 500, transition: 'all 0.15s',
                                    backgroundColor: filterMode === opt.key ? '#1A73E8' : 'transparent',
                                    color: filterMode === opt.key ? '#FFF' : '#5F6368',
                                }}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>

                    {/* 🔧 팝업 창으로 열기 버튼 */}
                    <button
                        onClick={() => {
                            const popupWidth = Math.round(window.screen.width * 0.85);
                            const popupHeight = Math.round(window.screen.height * 0.85);
                            const popupLeft = Math.round((window.screen.width - popupWidth) / 2);
                            const popupTop = Math.round((window.screen.height - popupHeight) / 2);
                            const popup = window.open('', '_blank',
                                `width=${popupWidth},height=${popupHeight},left=${popupLeft},top=${popupTop},menubar=no,toolbar=no,status=no,scrollbars=yes,resizable=yes`
                            );
                            if (!popup) { alert('팝업이 차단되었습니다. 브라우저 설정에서 팝업을 허용해주세요.'); return; }

                            // 메인 페이지의 스타일시트 복사
                            const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
                                .map(el => el.outerHTML).join('\n');

                            // 모달 내용 복제
                            const modalContent = document.querySelector('[data-cv-modal-content]');
                            if (!modalContent) { popup.close(); return; }

                            popup.document.write(`<!DOCTYPE html><html><head>
                                <title>교차 검증 비교 - DocuAI</title>
                                <meta charset="utf-8">
                                ${styles}
                                <style>
                                    body { margin: 0; overflow: hidden; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
                                    .cv-popup-root { width: 100vw; height: 100vh; display: flex; flex-direction: column; }
                                </style>
                            </head><body><div class="cv-popup-root">${modalContent.innerHTML}</div></body></html>`);
                            popup.document.close();

                            // AUIGrid 스크립트 복사 및 그리드 재생성
                            const auiScript = document.querySelector('script[src*="AUIGrid"]');
                            if (auiScript) {
                                const script = popup.document.createElement('script');
                                script.src = auiScript.src;
                                script.onload = () => {
                                    // 그리드는 정적 HTML로 복사되므로 스크롤/필터 기능은 제한됨
                                    console.log('📋 [CV Popup] AUIGrid 스크립트 로드 완료');
                                };
                                popup.document.head.appendChild(script);
                            }
                        }}
                        title="별도 팝업 창으로 열기 (메인 화면과 동시 사용 가능)"
                        style={{
                            padding: '3px 10px', borderRadius: 3, border: '1px solid #DADCE0', cursor: 'pointer',
                            fontSize: 11, fontWeight: 600, transition: 'all 0.15s',
                            backgroundColor: '#FFF', color: '#5F6368',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F1F3F4'; e.currentTarget.style.color = '#202124'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FFF'; e.currentTarget.style.color = '#5F6368'; }}
                    >
                        ↗ 팝업
                    </button>

                    <button onClick={onClose} style={{
                        background: '#D93025', border: 'none', borderRadius: 6,
                        color: '#FFF', padding: '5px 18px', cursor: 'pointer',
                        fontSize: 13, fontWeight: 600, letterSpacing: '0.3px',
                        boxShadow: '0 1px 3px rgba(217,48,37,0.3)',
                        transition: 'all 0.15s ease'
                    }}
                        onMouseEnter={(e) => { e.target.style.background = '#B3261E'; e.target.style.boxShadow = '0 2px 6px rgba(217,48,37,0.4)'; }}
                        onMouseLeave={(e) => { e.target.style.background = '#D93025'; e.target.style.boxShadow = '0 1px 3px rgba(217,48,37,0.3)'; }}
                    >
                        ✕ 닫기
                    </button>
                </div>
            </div>

            <div onClick={(e) => e.stopPropagation()} style={{
                height: 24, backgroundColor: '#F8F9FA',
                display: 'flex', alignItems: 'center', gap: 16,
                paddingLeft: 16, fontSize: 11, color: '#5F6368', flexShrink: 0,
                borderBottom: '1px solid #DADCE0', cursor: 'default'
            }}>
                <span style={{ color: '#D93025', fontWeight: 700 }}>abc</span>
                <span>다른 글자</span>
                <span>·</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <span style={{ display: 'inline-block', width: 12, height: 12, backgroundColor: '#E8F0FE', border: '1px solid #1A73E8', borderRadius: 2 }} />
                    선택 행
                </span>
                <span>· PDF + 그리드 비교 모드</span>
            </div>

            <div data-cv-modal-content onClick={(e) => e.stopPropagation()} style={{
                flex: 1, display: 'flex', flexDirection: 'column',
                backgroundColor: '#DADCE0', overflow: 'hidden', cursor: 'default'
            }}>

                {/* 📋 상단 불일치 요약 바 (열별 그룹핑 + 고유 패턴 상세) */}
                {showMismatchBar && mismatchList.length > 0 && (
                    <div style={{ flexShrink: 0, backgroundColor: '#FFF7ED', borderBottom: '1px solid #FED7AA' }}>
                        {/* 1단: 필드 칩 바 */}
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            padding: '5px 12px', overflowX: 'auto',
                            scrollbarWidth: 'thin'
                        }}>
                            <span style={{
                                fontSize: 12, fontWeight: 700, color: '#C2410C',
                                whiteSpace: 'nowrap', flexShrink: 0, marginRight: 2
                            }}>
                                ⚠ 불일치
                            </span>
                            <span style={{
                                fontSize: 11, color: '#9A3412', whiteSpace: 'nowrap',
                                flexShrink: 0, marginRight: 6
                            }}>
                                {mismatchByField.size}개 열 · {mismatchList.length}건
                            </span>
                            {Array.from(mismatchByField.entries()).map(([field, items]) => {
                                const isActive = activeFieldGroup === field;
                                const patterns = getFieldPatterns(field);
                                return (
                                    <button
                                        key={field}
                                        onClick={() => handleFieldClick(field)}
                                        title={`클릭: "${field}" 순차 탐색 (고유 패턴 ${patterns.length}개)`}
                                        style={{
                                            padding: '3px 10px', borderRadius: 12, cursor: 'pointer',
                                            fontSize: 12, fontWeight: isActive ? 700 : 500,
                                            whiteSpace: 'nowrap', flexShrink: 0,
                                            border: isActive ? '1.5px solid #1D4ED8' : '1px solid #D6D3D1',
                                            backgroundColor: isActive ? '#DBEAFE' : '#FFFFFF',
                                            color: isActive ? '#1D4ED8' : '#44403C',
                                            transition: 'all 0.12s',
                                            boxShadow: isActive ? '0 1px 3px rgba(29,78,216,0.15)' : 'none',
                                            display: 'flex', alignItems: 'center', gap: 4
                                        }}
                                        onMouseEnter={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = '#F5F5F4'; e.currentTarget.style.borderColor = '#A8A29E'; } }}
                                        onMouseLeave={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = '#FFFFFF'; e.currentTarget.style.borderColor = '#D6D3D1'; } }}
                                    >
                                        {field}
                                        <span style={{
                                            fontSize: 10, fontWeight: 700, borderRadius: 8,
                                            padding: '0 5px', lineHeight: '16px',
                                            backgroundColor: isActive ? '#1D4ED8' : '#E7E5E4',
                                            color: isActive ? '#FFF' : '#78716C'
                                        }}>
                                            {isActive ? `${fieldCursor + 1}/${items.length}` : items.length}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* 2단: 선택된 필드의 고유 값 패턴 상세 */}
                        {activeFieldGroup && mismatchByField.has(activeFieldGroup) && (
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                padding: '4px 12px 5px',
                                borderTop: '1px solid #FED7AA',
                                backgroundColor: '#FFFBF5', overflowX: 'auto',
                                scrollbarWidth: 'thin'
                            }}>
                                <span style={{
                                    fontSize: 11, fontWeight: 600, color: '#78350F',
                                    whiteSpace: 'nowrap', flexShrink: 0
                                }}>
                                    {activeFieldGroup} 패턴:
                                </span>
                                {getFieldPatterns(activeFieldGroup).map((pattern, pIdx) => {
                                    const pKey = `${activeFieldGroup}|||${pattern.leftVal}|||${pattern.rightVal}`;
                                    const isActive = activePatternKey === pKey;
                                    const leftDisplay = pattern.leftVal || '(없음)';
                                    const rightDisplay = pattern.rightVal || '(없음)';
                                    return (
                                        <button
                                            key={pIdx}
                                            onClick={() => handlePatternClick(activeFieldGroup, pattern)}
                                            title={`클릭: 이 패턴의 행 순차 탐색\n행: ${pattern.rows.map(r => r + 1).join(', ')}`}
                                            style={{
                                                padding: '2px 8px', borderRadius: 8, cursor: 'pointer',
                                                fontSize: 11, fontWeight: isActive ? 600 : 400,
                                                whiteSpace: 'nowrap', flexShrink: 0,
                                                border: isActive ? '1.5px solid #7C3AED' : '1px solid #E5E7EB',
                                                backgroundColor: isActive ? '#EDE9FE' : '#FFFFFF',
                                                color: isActive ? '#6D28D9' : '#4B5563',
                                                transition: 'all 0.12s',
                                                display: 'flex', alignItems: 'center', gap: 3
                                            }}
                                            onMouseEnter={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = '#F5F3FF'; } }}
                                            onMouseLeave={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = '#FFFFFF'; } }}
                                        >
                                            <span style={{ color: '#DC2626' }}>{leftDisplay}</span>
                                            <span style={{ color: '#9CA3AF', fontSize: 10 }}>→</span>
                                            <span style={{ color: '#2563EB' }}>{rightDisplay}</span>
                                            <span style={{
                                                fontSize: 9, fontWeight: 700, borderRadius: 6,
                                                padding: '0 4px', lineHeight: '14px', marginLeft: 2,
                                                backgroundColor: isActive ? '#7C3AED' : '#F3F4F6',
                                                color: isActive ? '#FFF' : '#6B7280'
                                            }}>
                                                {isActive ? `${patternCursor + 1}/${pattern.rows.length}` : `×${pattern.rows.length}`}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* 메인 영역: 좌=PDF뷰어 | 우=상하 그리드 */}
                <div ref={containerRef} style={{
                    flex: 1, display: 'flex', flexDirection: 'row',
                    overflow: 'hidden', minWidth: 0
                }}>
                    {/* 좌측: PDF 뷰어 */}
                    <div style={{
                        width: `${leftPanelWidth}%`, flexShrink: 0,
                        display: 'flex', flexDirection: 'column',
                        backgroundColor: '#F5F5F5', overflow: 'hidden'
                    }}>
                        <div style={{
                            height: 26, backgroundColor: '#FFF', color: '#202124',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            paddingLeft: 10, paddingRight: 10,
                            fontSize: 12, fontWeight: 500, flexShrink: 0,
                            borderBottom: '1px solid #DADCE0', borderLeft: '3px solid #0D9488'
                        }}>
                            <span>📄 원본 문서</span>
                            {pdfTotalPages > 0 && (
                                <span style={{ fontSize: 11, color: '#5F6368' }}>
                                    {pdfPageNumber} / {pdfTotalPages} 페이지
                                </span>
                            )}
                        </div>
                        <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
                            {pdfFile ? (
                                <ReactPDFViewer
                                    file={pdfFile}
                                    pageNumber={pdfPageNumber}
                                    onLoadSuccess={(info) => setPdfTotalPages(info.numPages)}
                                    onPageChange={(page) => setPdfPageNumber(page)}
                                />
                            ) : (
                                <div style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    height: '100%', color: '#9CA3AF', fontSize: 13
                                }}>
                                    PDF 파일이 없습니다
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 🔧 드래그 리사이저 (좌우 경계선) */}
                    <div
                        onMouseDown={handleSplitterMouseDown}
                        style={{
                            width: 5, flexShrink: 0, cursor: 'col-resize',
                            backgroundColor: '#DADCE0', position: 'relative',
                            transition: 'background-color 0.15s',
                            zIndex: 10
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1A73E8'; }}
                        onMouseLeave={(e) => { if (!isDraggingRef.current) e.currentTarget.style.backgroundColor = '#DADCE0'; }}
                    >
                        <div style={{
                            position: 'absolute', top: '50%', left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 3, height: 30, borderRadius: 2,
                            backgroundColor: '#9CA3AF', opacity: 0.6
                        }} />
                    </div>

                    {/* 우측: 상하 그리드 */}
                    <div style={{
                        flex: 1, display: 'flex', flexDirection: 'column',
                        overflow: 'hidden', minWidth: 0, gap: 1,
                        backgroundColor: '#DADCE0'
                    }}>
                        {/* 우상: 1차 모델 (기준) 그리드 */}
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
                            <div style={{
                                height: 26, backgroundColor: '#FFF', color: '#202124',
                                display: 'flex', alignItems: 'center', paddingLeft: 10,
                                fontSize: 12, fontWeight: 500, flexShrink: 0,
                                borderBottom: '1px solid #DADCE0', borderLeft: '3px solid #1A73E8'
                            }}>
                                1차 모델 (기준): {primaryModel}
                            </div>
                            <div id="cv_left_grid" style={{ flex: 1, backgroundColor: '#FFF' }} />
                        </div>

                        {/* 우하: 2차 모델 (비교) 그리드 */}
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
                            <div style={{
                                height: 26, backgroundColor: '#FFF', color: '#202124',
                                display: 'flex', alignItems: 'center', paddingLeft: 10,
                                fontSize: 12, fontWeight: 500, flexShrink: 0,
                                borderBottom: '1px solid #DADCE0', borderLeft: '3px solid #5F6368'
                            }}>
                                2차 모델 (비교): {secondModel}
                            </div>
                            <div id="cv_right_grid" style={{ flex: 1, backgroundColor: '#FFF' }} />
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .cv-mismatch-left {
                    background-color: #FFF8F8 !important;
                    font-weight: 500 !important;
                }
                .cv-mismatch-right {
                    background-color: #FFF8F8 !important;
                    font-weight: 500 !important;
                }
                .cv-diff-del-left {
                    color: #D93025 !important;
                    text-decoration: line-through;
                    font-weight: 600;
                    padding: 0 1px;
                }
                .cv-diff-add-right {
                    color: #1A73E8 !important;
                    font-weight: 600;
                    padding: 0 1px;
                }
                .cv-diff-add {
                    color: #1A73E8 !important;
                    font-weight: 600;
                    padding: 0 1px;
                }
                .cv-diff-empty {
                    color: #9AA0A6;
                    font-style: italic;
                    font-size: 0.85em;
                }
                .cv-synced-row {
                    background-color: #E8F0FE !important;
                    outline: 1px solid #1A73E8 !important;
                    outline-offset: -1px;
                }
            `}</style>
        </div >
    );
};

export default CrossValidationModal;
